import React, { useState, useEffect, useMemo } from 'react';
import { Save, X } from 'lucide-react';
import { TYPES_FORMALITE, ETAPES, ENREGISTREMENTS } from '../constants/formaliteOptions';
import { useClients } from '../hooks/useClients';
import { useCompanies } from '../hooks/useCompanies';
import { calculateDeadline, calculateTaxDeadline } from '../utils/deadlineCalculator';

interface EditCellModalProps {
  isOpen: boolean;
  field: string;
  value: any;
  onSave: (newValue: any) => Promise<void>;
  onClose: () => void;
  currentRow?: any; // Données complètes de la ligne en cours d'édition
}

const EditCellModal: React.FC<EditCellModalProps> = ({ isOpen, field, value, onSave, onClose, currentRow }) => {
  const [editValue, setEditValue] = useState<string>('');
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState<string>('');
  const { clients, loading: clientsLoading } = useClients();
  const { companies, loading: companiesLoading } = useCompanies();
  const [saving, setSaving] = useState(false);

  // Options triées et filtrées pour les selects
  const sortedClients = useMemo(() => {
    return [...clients].sort((a, b) => {
      const nameA = a.field_7245 || '';
      const nameB = b.field_7245 || '';
      return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
    });
  }, [clients]);

  const sortedCompanies = useMemo(() => {
    return [...companies].sort((a, b) => {
      const nameA = a.field_7248 || '';
      const nameB = b.field_7248 || '';
      return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
    });
  }, [companies]);

  const sortedTypes = useMemo(() => {
    return [...TYPES_FORMALITE].sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  }, []);

  const filteredClients = useMemo(() => {
    if (!searchTerm) return sortedClients;
    const search = searchTerm.toLowerCase();
    return sortedClients.filter(client => 
      client.field_7245?.toLowerCase().includes(search)
    );
  }, [sortedClients, searchTerm]);

  const filteredCompanies = useMemo(() => {
    if (!searchTerm) return sortedCompanies;
    const search = searchTerm.toLowerCase();
    return sortedCompanies.filter(company => 
      company.field_7248?.toLowerCase().includes(search)
    );
  }, [sortedCompanies, searchTerm]);

  const filteredTypes = useMemo(() => {
    if (!searchTerm) return sortedTypes;
    const search = searchTerm.toLowerCase();
    return sortedTypes.filter(type => 
      type.toLowerCase().includes(search)
    );
  }, [sortedTypes, searchTerm]);

  // Initialiser la valeur
  useEffect(() => {
    if (isOpen) {
      setSearchTerm(''); // Réinitialiser la recherche à chaque ouverture
      if (field === 'field_7263') {
        // Pour Type, on initialise comme multi-sélection
        if (Array.isArray(value)) {
          const types = value.map((v: any) => v.value || v).filter(Boolean);
          setSelectedTypes(new Set(types));
          setEditValue('');
        } else {
          setSelectedTypes(new Set());
          setEditValue('');
        }
      } else {
        if (Array.isArray(value)) {
          setEditValue(value.map((v: any) => v.id || v).join(','));
        } else if (typeof value === 'object' && value?.id) {
          setEditValue(value.id);
        } else {
          setEditValue(value?.toString() || '');
        }
      }
    }
  }, [isOpen, value, field]);

  const handleSave = async () => {
    setSaving(true);
    try {
      let newValue: any = editValue;
      const updateData: any = {};
      
      // Conversion selon le type de champ
      if (field === 'field_7260') {
        // Customer (link_row): convertir les IDs
        newValue = editValue.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      } else if (field === 'field_7261') {
        // Company (link_row): convertir les IDs
        newValue = editValue.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      } else if (field === 'field_7263') {
        // Formality Type (link_row): convertir les types sélectionnés en array
        // Pour les link_row, on doit envoyer les IDs, mais ici on gère juste les valeurs string
        newValue = Array.from(selectedTypes);
      } else if (field === 'field_7265') {
        // Progression (string): garder la chaîne
        newValue = editValue;
      } else if (field === 'field_7272') {
        // Tax (single_select): garder la chaîne
        newValue = editValue;
      } else if (field === 'field_7262') {
        // Description: garder la chaîne
        newValue = editValue;
      } else if (field === 'field_7251') {
        // Name: garder la chaîne
        newValue = editValue;
      } else if (field === 'field_7270') {
        // Forced Deadline (date): envoyer null si vide, sinon la chaîne
        newValue = editValue.trim() === '' ? null : editValue;
      } else if (field === 'field_7269') {
        // Deadline (date): envoyer null si vide, sinon la chaîne
        newValue = editValue.trim() === '' ? null : editValue;
      } else if (field === 'field_7271') {
        // Tax Deadline (date): envoyer null si vide, sinon la chaîne
        newValue = editValue.trim() === '' ? null : editValue;
      } else if (field === 'field_7274') {
        // Sender: garder la chaîne
        newValue = editValue;
      }
      
      // Ajouter la valeur modifiée
      updateData[field] = newValue;
      
      // CALCUL DES ÉCHÉANCES AUTOMATIQUES
      // Si on modifie la Progression (field_7265)
      if (field === 'field_7265' && currentRow) {
        const newStatus = editValue;
        // Extraire les types de formalité
        const formalityTypes = Array.isArray(currentRow.field_7263) 
          ? currentRow.field_7263.map((t: any) => t.value || t)
          : [];
        
        // Calculer l'échéance automatique principale
        const autoDeadline = calculateDeadline(newStatus, formalityTypes);
        if (autoDeadline) {
          updateData.field_7269 = autoDeadline; // Mettre à jour Échéance Auto
        }
      }
      
      // Si on modifie le Statut Impôts (field_7272)
      if (field === 'field_7272' && currentRow) {
        const newTaxStatus = editValue;
        // Extraire les types de formalité
        const formalityTypes = Array.isArray(currentRow.field_7263) 
          ? currentRow.field_7263.map((t: any) => t.value || t)
          : [];
        
        // Calculer l'échéance impôts automatique
        const taxDeadline = calculateTaxDeadline(newTaxStatus, formalityTypes);
        if (taxDeadline) {
          updateData.field_7271 = taxDeadline; // Mettre à jour Échéance Impôts
        } else if (newTaxStatus !== "Envoyé") {
          // Si le statut n'est plus "Envoyé", on peut optionnellement effacer l'échéance impôts
          // (décommentez la ligne suivante si souhaité)
          // updateData.field_7271 = null;
        }
      }
      
      // Si on modifie les Types de Formalité (field_7263) ET que la progression actuelle nécessite une échéance
      if (field === 'field_7263' && currentRow) {
        const currentStatus = currentRow.field_7265;
        const newFormalityTypes = Array.from(selectedTypes);
        
        // Recalculer l'échéance principale si le statut actuel en nécessite une
        if (currentStatus) {
          const autoDeadline = calculateDeadline(currentStatus, newFormalityTypes);
          if (autoDeadline) {
            updateData.field_7269 = autoDeadline;
          }
        }
        
        // Recalculer l'échéance impôts si le statut impôts est "Envoyé"
        const currentTaxStatus = currentRow.field_7272?.value || currentRow.field_7272;
        if (currentTaxStatus === "Envoyé") {
          const taxDeadline = calculateTaxDeadline(currentTaxStatus, newFormalityTypes);
          if (taxDeadline) {
            updateData.field_7271 = taxDeadline;
          }
        }
      }
      
      // Sauvegarder toutes les modifications (incluant les échéances calculées)
      await onSave(updateData);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const getFieldLabel = (fieldName: string) => {
    const labels: Record<string, string> = {
      field_7261: 'Entreprise',
      field_7263: 'Type',
      field_7260: 'Client',
      field_7265: 'Progression',
      field_7251: 'Nom',
      field_7262: 'Description',
      field_7272: 'Statut',
      field_7270: 'Échéance forcée',
      field_7269: 'Échéance',
      field_7271: 'Échéance fiscale',
      field_7274: 'Sender',
    };
    return labels[fieldName] || fieldName;
  };

  const renderInput = () => {
    if (field === 'field_7263') {
      // Formality Type: multi-select avec checkboxes et recherche
      return (
        <div className="space-y-2">
          <input
            type="text"
            placeholder="Rechercher un type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 mb-2"
          />
          <div className="max-h-64 overflow-y-auto border border-blue-300 rounded p-3 bg-blue-50">
            {filteredTypes.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-2">Aucun type trouvé</p>
            ) : (
              filteredTypes.map((type) => (
                <label key={type} className="flex items-center space-x-2 cursor-pointer hover:bg-white p-2 rounded">
                  <input
                    type="checkbox"
                    checked={selectedTypes.has(type)}
                    onChange={(e) => {
                      const newTypes = new Set(selectedTypes);
                      if (e.target.checked) {
                        newTypes.add(type);
                      } else {
                        newTypes.delete(type);
                      }
                      setSelectedTypes(newTypes);
                    }}
                    className="w-4 h-4 text-blue-600 rounded cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">{type}</span>
                </label>
              ))
            )}
          </div>
        </div>
      );
    } else if (field === 'field_7265') {
      // Progression: select (string - formerly was select but now string)
      return (
        <select
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          className="w-full px-3 py-2 border border-blue-300 rounded focus:outline-none focus:border-blue-500"
          autoFocus
        >
          <option value="">- Sélectionner -</option>
          {ETAPES.map((etape) => (
            <option key={etape} value={etape}>
              {etape}
            </option>
          ))}
        </select>
      );
    } else if (field === 'field_7272') {
      // Tax: select (single_select)
      return (
        <select
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          className="w-full px-3 py-2 border border-blue-300 rounded focus:outline-none focus:border-blue-500"
          autoFocus
        >
          <option value="">- Sélectionner -</option>
          {ENREGISTREMENTS.map((enr) => (
            <option key={enr} value={enr}>
              {enr}
            </option>
          ))}
        </select>
      );
    } else if (field === 'field_7260') {
      // Customer: select avec recherche
      return (
        <div className="space-y-2">
          <input
            type="text"
            placeholder="Rechercher un client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
          />
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-full px-3 py-2 border border-blue-300 rounded focus:outline-none focus:border-blue-500"
            size={Math.min(filteredClients.length + 1, 10)}
          >
            <option value="">- Sélectionner -</option>
            {filteredClients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.field_7245 || `Client ${client.id}`}
              </option>
            ))}
          </select>
        </div>
      );
    } else if (field === 'field_7261') {
      // Company: select avec recherche
      return (
        <div className="space-y-2">
          <input
            type="text"
            placeholder="Rechercher une entreprise..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
          />
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-full px-3 py-2 border border-blue-300 rounded focus:outline-none focus:border-blue-500"
            size={Math.min(filteredCompanies.length + 1, 10)}
          >
            <option value="">- Sélectionner -</option>
            {filteredCompanies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.field_7248 || `Entreprise ${company.id}`}
              </option>
            ))}
          </select>
        </div>
      );
    } else if (field === 'field_7262') {
      // Description: textarea
      return (
        <textarea
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          className="w-full px-3 py-2 border border-blue-300 rounded focus:outline-none focus:border-blue-500"
          rows={5}
          autoFocus
        />
      );
    } else if (field === 'field_7274') {
      // Sender: textarea
      return (
        <textarea
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          className="w-full px-3 py-2 border border-blue-300 rounded focus:outline-none focus:border-blue-500"
          rows={5}
          autoFocus
        />
      );
    } else if (field === 'field_7270' || field === 'field_7269' || field === 'field_7271') {
      // Dates (forced_deadline, deadline, tax_deadline): date input
      return (
        <input
          type="date"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          className="w-full px-3 py-2 border border-blue-300 rounded focus:outline-none focus:border-blue-500"
          autoFocus
        />
      );
    } else {
      // Texte standard (Name, Entreprise, etc)
      return (
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          className="w-full px-3 py-2 border border-blue-300 rounded focus:outline-none focus:border-blue-500"
          autoFocus
        />
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Modifier: {getFieldLabel(field)}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {getFieldLabel(field)}
            </label>
            {renderInput()}
          </div>

          {field === 'field_7263' && selectedTypes.size > 0 && (
            <div className="bg-green-50 border border-green-200 rounded p-3">
              <p className="text-xs font-medium text-green-700 mb-2">Sélectionnés ({selectedTypes.size}):</p>
              <div className="flex flex-wrap gap-2">
                {Array.from(selectedTypes).map((type) => (
                  <span key={type} className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                    {type}
                    <button
                      onClick={() => {
                        const newTypes = new Set(selectedTypes);
                        newTypes.delete(type);
                        setSelectedTypes(newTypes);
                      }}
                      className="text-green-600 hover:text-green-800 font-bold ml-1"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCellModal;
